// ============================================================================
// Field work foundations — activity types, worker roster, labour providers,
// crews. Phase 0 of docs/harvest-and-labour-plan.md; served by
// /api/field-work (HHNodeServer routes/fieldWork.js).
// ============================================================================

export type ActivityCategory =
  | 'harvest' | 'canopy' | 'crop_protection' | 'ground' | 'planting'
  | 'irrigation' | 'machinery' | 'maintenance' | 'general';

export type WorkUnit =
  | 'hours' | 'trees' | 'rows' | 'hectares' | 'bins' | 'kg' | 'tonnes' | 'loads' | 'bales';

export type PayBasis = 'per_hour' | 'per_day' | 'per_unit' | 'per_ha' | 'flat';

export type WorkerType = 'employee' | 'casual' | 'contractor_worker';

export interface ActivityType {
  id: number;
  organisation_id: number | null; // null = built-in
  type_key: string;
  name: string;
  category: ActivityCategory;
  work_unit: WorkUnit;
  default_pay_basis: PayBasis;
  uses_gps_coverage: boolean;
  uses_containers: boolean;
  presence_pings: boolean;
  is_builtin: boolean;
  active: boolean;
  sort_order: number;
}

export interface ActivityTypeInput {
  name: string;
  category: ActivityCategory;
  work_unit?: WorkUnit;
  default_pay_basis?: PayBasis;
  uses_gps_coverage?: boolean;
  uses_containers?: boolean;
  presence_pings?: boolean;
  active?: boolean;
}

export interface WorkerCrewRef {
  crew_id: number;
  crew_name: string;
}

export interface Worker {
  id: number;
  organisation_id: number;
  user_id: number | null;
  user_email: string | null;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  worker_type: WorkerType;
  labour_provider_id: number | null;
  labour_provider_name: string | null;
  picker_code: string;
  phone: string | null;
  start_date: string | null;
  end_date: string | null;
  notes: string | null;
  active: boolean;
  crews: WorkerCrewRef[] | null;
  created_at: string;
  updated_at: string;
}

export interface WorkerInput {
  first_name: string;
  last_name?: string;
  preferred_name?: string | null;
  worker_type?: WorkerType;
  labour_provider_id?: number | null;
  user_id?: number | null;
  /** Leave empty to have one generated (W0001-3 style). */
  picker_code?: string | null;
  phone?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
  active?: boolean;
}

export interface LabourProvider {
  id: number;
  organisation_id: number;
  name: string;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  business_number: string | null;
  notes: string | null;
  active: boolean;
  worker_count: number;
}

export interface LabourProviderInput {
  name: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  business_number?: string | null;
  notes?: string | null;
  active?: boolean;
}

export interface CrewMember {
  worker_id: number;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  picker_code: string;
  worker_type: WorkerType;
  joined_on: string;
}

export interface Crew {
  id: number;
  organisation_id: number;
  name: string;
  labour_provider_id: number | null;
  labour_provider_name: string | null;
  leader_worker_id: number | null;
  leader_name: string | null;
  season_year: number | null;
  notes: string | null;
  active: boolean;
  members: CrewMember[];
}

export interface CrewInput {
  name: string;
  labour_provider_id?: number | null;
  leader_worker_id?: number | null;
  season_year?: number | null;
  notes?: string | null;
  member_worker_ids?: number[];
  active?: boolean;
}

export const ACTIVITY_CATEGORY_LABELS: Record<ActivityCategory, string> = {
  harvest: 'Harvest',
  canopy: 'Canopy (pruning, thinning, training)',
  crop_protection: 'Crop protection',
  ground: 'Ground & soil',
  planting: 'Planting',
  irrigation: 'Irrigation',
  machinery: 'Machinery',
  maintenance: 'Maintenance',
  general: 'General',
};

export const WORK_UNIT_LABELS: Record<WorkUnit, string> = {
  hours: 'Hours', trees: 'Trees', rows: 'Rows', hectares: 'Hectares', bins: 'Bins',
  kg: 'Kilograms', tonnes: 'Tonnes', loads: 'Loads', bales: 'Bales',
};

export const WORKER_TYPE_LABELS: Record<WorkerType, string> = {
  employee: 'Employee',
  casual: 'Casual',
  contractor_worker: 'Contractor',
};

export function workerDisplayName(w: Pick<Worker, 'first_name' | 'last_name' | 'preferred_name'>): string {
  return `${w.preferred_name || w.first_name} ${w.last_name || ''}`.trim();
}
